import React from 'react';
import { OrdemDeServicoFormData } from '../../types/ordemServico';
import { calculateHoraTotal } from './useOrdemServicoCalculations';
import { ClientePedidoSearchSelect, PedidoClienteOption } from './ClientePedidoSearchSelect';
import { User, Calendar, Clock, MapPin, Tag, Users, PlusCircle } from 'lucide-react';
import { PropostaSaldoBanner } from './PropostaSaldoBanner';
import { PropostaSaldoInfo } from '../../services/propostaSaldoService';

interface OsFormInformacoesBasicasProps {
  formData: OrdemDeServicoFormData;
  setFormData: React.Dispatch<React.SetStateAction<OrdemDeServicoFormData>>;
  pedidosOptions: PedidoClienteOption[];
  statusOptions: { id: number; status: string; cor: string }[];
  servicosOptions: { id: string; servicos: string; valor_unitario: number }[];
  ajudantesOptions: { id: string; servicos: string; valor_unitario: number }[];
  loadingDropdowns: boolean;
  saldoProposta?: PropostaSaldoInfo | null;
  loadingSaldo?: boolean;
  bloqueioSemSaldoAtivo?: boolean;
}

export const OsFormInformacoesBasicas: React.FC<OsFormInformacoesBasicasProps> = ({
  formData,
  setFormData,
  pedidosOptions,
  statusOptions,
  servicosOptions,
  ajudantesOptions,
  loadingDropdowns,
  saldoProposta,
  loadingSaldo = false,
  bloqueioSemSaldoAtivo = false,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let finalValue: any = value;

    if (type === 'number') {
      finalValue = value === '' ? 0 : parseFloat(value);
    }

    setFormData((prev) => {
      const next = { ...prev, [name]: finalValue };

      // Se alterou horário inicial ou final, recalcula hora_total
      if (name === 'horario_inicio' || name === 'horario_final') {
        const hInicio = name === 'horario_inicio' ? value : prev.horario_inicio;
        const hFinal = name === 'horario_final' ? value : prev.horario_final;
        next.hora_total = calculateHoraTotal(hInicio, hFinal);
      }

      return next;
    });
  };

  // Handler para seleção do Pedido/Cliente com busca
  const handleSelectClientePedido = (pedidoId: string) => {
    setFormData((prev) => ({
      ...prev,
      pedido_id: pedidoId,
    }));
  };

  // Quando escolhe o serviço principal, atualiza automaticamente valor_unit_servi
  const handleSelectServico = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const found = servicosOptions.find((s) => s.id.toString() === selectedId);
    const unitVal = found ? Number(found.valor_unitario) : 0;

    setFormData((prev) => ({
      ...prev,
      item_servico: selectedId,
      valor_unit_servi: unitVal,
    }));
  };

  // Quando escolhe o ajudante principal, atualiza automaticamente valor_unit_ajud
  const handleSelectAjudante = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const found = ajudantesOptions.find((a) => a.id.toString() === selectedId);
    const unitVal = found ? Number(found.valor_unitario) : 0;

    setFormData((prev) => ({
      ...prev,
      item_ajudante: selectedId,
      valor_unit_ajud: unitVal,
    }));
  };

  return (
    <div className="space-y-5">
      {/* Linha 1: Cliente & Status */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
        {formData.id && (
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
              ID O.S
            </label>
            <input
              type="text"
              readOnly
              value={`#${formData.id}`}
              className="w-full bg-slate-100 border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 outline-none"
            />
          </div>
        )}

        <div className={formData.id ? 'sm:col-span-6' : 'sm:col-span-8'}>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1 flex items-center justify-between">
            <span className="flex items-center gap-1">
              <User className="w-3.5 h-3.5 text-red-600" />
              Cliente / Pedido <span className="text-red-500">*</span>
            </span>
            <span className="text-3xs text-slate-500 font-normal lowercase">
              (Digite para buscar pelo nome, fantasia, documento ou pedido)
            </span>
          </label>

          {/* Componente de Busca Interativa de Cliente / Pedido */}
          <ClientePedidoSearchSelect
            value={formData.pedido_id}
            onChange={handleSelectClientePedido}
            options={pedidosOptions}
            loading={loadingDropdowns}
            required
            placeholder="Digite para buscar cliente, nome fantasia, documento ou pedido..."
          />

          {/* Banner Informativo / Alerta de Saldo de Caminhões da Proposta */}
          <PropostaSaldoBanner
            saldo={saldoProposta || null}
            loading={loadingSaldo}
            bloqueioAtivo={bloqueioSemSaldoAtivo}
            selectedItemServicoId={formData.item_servico}
            selectedItemAjudanteId={formData.item_ajudante}
          />
        </div>

        <div className="sm:col-span-4">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Status <span className="text-red-500">*</span>
          </label>
          <select
            name="status_id"
            value={formData.status_id}
            onChange={handleChange}
            required
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white font-medium"
          >
            <option value="">Selecione o status...</option>
            {statusOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.status}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Linha 2: Data & Horários */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl">
        <div>
          <label className="block text-xs font-bold text-slate-600 uppercase mb-1 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-slate-500" />
            Data Atendimento <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="data_atendimento"
            value={formData.data_atendimento}
            onChange={handleChange}
            required
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium bg-white outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-600 uppercase mb-1 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-slate-500" />
            Horário Início
          </label>
          <input
            type="time"
            name="horario_inicio"
            value={formData.horario_inicio}
            onChange={handleChange}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium bg-white outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-600 uppercase mb-1 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-slate-500" />
            Horário Final
          </label>
          <input
            type="time"
            name="horario_final"
            value={formData.horario_final}
            onChange={handleChange}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium bg-white outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-600 uppercase mb-1">
            Total de Horas
          </label>
          <input
            type="text"
            readOnly
            name="hora_total"
            value={formData.hora_total}
            placeholder="00:00"
            className="w-full bg-slate-100 border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-bold text-red-600 outline-none"
          />
        </div>
      </div>

      {/* Linha 3: Endereços */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-emerald-600" />
            Endereço de Retirada
          </label>
          <textarea
            name="retirada"
            rows={2}
            value={formData.retirada}
            onChange={handleChange}
            placeholder="Informe o local de coleta..."
            className="w-full border border-slate-300 rounded-lg p-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-red-600" />
            Endereço de Entrega
          </label>
          <textarea
            name="entrega"
            rows={2}
            value={formData.entrega}
            onChange={handleChange}
            placeholder="Informe o local de entrega..."
            className="w-full border border-slate-300 rounded-lg p-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>
      </div>

      {/* Linha 4: Veículo Solicitado / Serviço */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl">
        <div className="sm:col-span-8">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1 flex items-center justify-between">
            <span className="flex items-center gap-1">
              <Tag className="w-3.5 h-3.5 text-slate-500" />
              Veículo / Serviço Solicitado
            </span>
          </label>
          <select
            name="item_servico"
            value={formData.item_servico}
            onChange={handleSelectServico}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white font-medium"
          >
            <option value="">Selecione o serviço...</option>
            {servicosOptions.map((s) => {
              const itemInfo = saldoProposta?.itens.find((it) => String(it.id) === String(s.id));
              const saldoTxt = itemInfo && itemInfo.quantidadeContratada > 0
                ? itemInfo.saldoRestante <= 0
                  ? `(Saldo: 0 ESGOTADO - ${itemInfo.quantidadeUtilizada}/${itemInfo.quantidadeContratada})`
                  : `(Saldo: ${itemInfo.saldoRestante} disp. de ${itemInfo.quantidadeContratada})`
                : '';

              return (
                <option key={s.id} value={s.id}>
                  {s.servicos} {saldoTxt}
                </option>
              );
            })}
          </select>
        </div>

        <div className="sm:col-span-4">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Valor Serviço (R$)
          </label>
          <input
            type="number"
            step="0.01"
            name="valor_unit_servi"
            value={formData.valor_unit_servi}
            onChange={handleChange}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white"
          />
        </div>
      </div>

      {/* Linha 5: Ajudantes & Valores */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl">
        <div className="sm:col-span-4">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1 flex items-center gap-1">
            <Users className="w-3.5 h-3.5 text-slate-500" />
            Serviço de Ajudante
          </label>
          <select
            name="item_ajudante"
            value={formData.item_ajudante}
            onChange={handleSelectAjudante}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white font-medium"
          >
            <option value="">Nenhum ajudante contratado...</option>
            {ajudantesOptions.map((a) => {
              const itemInfo = saldoProposta?.itens.find((it) => String(it.id) === String(a.id));
              const saldoTxt = itemInfo && itemInfo.quantidadeContratada > 0
                ? itemInfo.saldoRestante <= 0
                  ? `(Saldo: 0 ESGOTADO - ${itemInfo.quantidadeUtilizada}/${itemInfo.quantidadeContratada})`
                  : `(Saldo: ${itemInfo.saldoRestante} disp. de ${itemInfo.quantidadeContratada})`
                : '';

              return (
                <option key={a.id} value={a.id}>
                  {a.servicos} {saldoTxt}
                </option>
              );
            })}
          </select>
        </div>

        <div className="sm:col-span-3">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Valor Unit. (R$)
          </label>
          <input
            type="number"
            step="0.01"
            readOnly
            name="valor_unit_ajud"
            value={formData.valor_unit_ajud}
            className="w-full bg-slate-100 border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 outline-none"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Quantidade
          </label>
          <input
            type="number"
            min="0"
            name="quantidade_ajudante"
            value={formData.quantidade_ajudante}
            onChange={handleChange}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white"
          />
        </div>

        <div className="sm:col-span-3">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Subtotal Viagem (R$)
          </label>
          <input
            type="number"
            step="0.01"
            readOnly
            name="subtotal_viagem"
            value={formData.subtotal_viagem}
            className="w-full bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm font-bold text-red-700 outline-none"
          />
        </div>
      </div>

      {/* Linha 6: Extras & Adicionais */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
        <div className="sm:col-span-8">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1 flex items-center gap-1">
            <PlusCircle className="w-3.5 h-3.5 text-slate-500" />
            Detalhes Extras / Adicionais
          </label>
          <input
            type="text"
            name="detalhes_adicional"
            value={formData.detalhes_adicional}
            onChange={handleChange}
            placeholder="Ex: Hora extra, parada adicional..."
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>

        <div className="sm:col-span-4">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
            Valor Adicional (R$)
          </label>
          <input
            type="number"
            step="0.01"
            name="adicional"
            value={formData.adicional}
            onChange={handleChange}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>
      </div>

      {/* Linha 7: Descrição do Material */}
      <div>
        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
          Descrição dos Materiais / Observações
        </label>
        <textarea
          name="descricao"
          rows={3}
          value={formData.descricao}
          onChange={handleChange}
          placeholder="Descreva a carga ou materiais transportados..."
          className="w-full border border-slate-300 rounded-lg p-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 outline-none"
        />
      </div>
    </div>
  );
};
